import { CLINICIAN_PHOTO_BACKGROUND, CLINICIAN_PHOTO_PX } from '@lighthouse/shared';

const MAX_PHOTO_BYTES = 1_500_000;

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error('Could not prepare that photo'))),
      'image/jpeg',
      quality
    );
  });
}

function fileFromBytes(bytes: Uint8Array): File {
  const data = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(data).set(bytes);
  const jpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
  const png = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
  const webp =
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50;
  if (jpeg) return new File([data], 'profile.jpg', { type: 'image/jpeg' });
  if (png) return new File([data], 'profile.png', { type: 'image/png' });
  if (webp) return new File([data], 'profile.webp', { type: 'image/webp' });
  throw new Error('Could not prepare that photo. Use a JPG, PNG, or WebP.');
}

/** Crop a clinician photo to the shared square spec before upload. */
export async function prepareClinicianPhoto(file: File): Promise<File> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('That photo could not be read. Use a JPG, PNG, or WebP.');
  }

  const canvas = document.createElement('canvas');
  canvas.width = CLINICIAN_PHOTO_PX;
  canvas.height = CLINICIAN_PHOTO_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not prepare that photo');
  ctx.fillStyle = CLINICIAN_PHOTO_BACKGROUND;
  ctx.fillRect(0, 0, CLINICIAN_PHOTO_PX, CLINICIAN_PHOTO_PX);

  const scale = Math.max(CLINICIAN_PHOTO_PX / bitmap.width, CLINICIAN_PHOTO_PX / bitmap.height);
  const width = bitmap.width * scale;
  const height = bitmap.height * scale;
  // A dead-center crop clips the crown. Keep a little more of the top.
  const overflowY = Math.max(0, height - CLINICIAN_PHOTO_PX);
  const y = overflowY > 0 ? -overflowY * 0.34 : (CLINICIAN_PHOTO_PX - height) / 2;
  ctx.drawImage(bitmap, (CLINICIAN_PHOTO_PX - width) / 2, y, width, height);
  bitmap.close();

  let quality = 0.9;
  let blob = await toBlob(canvas, quality);
  while (blob.size > MAX_PHOTO_BYTES && quality > 0.5) {
    quality = Math.round((quality - 0.1) * 10) / 10;
    blob = await toBlob(canvas, quality);
  }
  if (blob.size > MAX_PHOTO_BYTES) {
    throw new Error('That photo is too large. Try a smaller image.');
  }

  const bytes = new Uint8Array(await blob.arrayBuffer());
  return fileFromBytes(bytes);
}
